import React from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Icon } from 'components/ui';
import TextInput from 'components/Inputs/TextInput/TextInput';
import FormValidator from 'utils/FormValidator';
import ErrorConstants from 'utils/ErrorConstants';
import ErrorsUtils from 'utils/Errors';
import localeUtils from 'utils/LanguageLocale';
import UI from 'utils/UI';
import { colors, space } from 'style/config';

const SEARCH_ICON_SIZE = 16;

function FirstAddressField({
    address1 = '',
    isAdsRestricted = false,
    isAddressVerified = false,
    hasAVS = false,
    isShortenedLoqateForm = false,
    highlightedAddrIndex = -1,
    hasAddress1Focus = false,
    hasLoqateError = false,
    isFCReleaseThreeEnabled = false,
    isSubscription = false,
    modalTitle = '',
    localization = {},
    onKeyUp,
    onBlur,
    onFocus,
    onKeyDown,
    onChange,
    addressInputRef,
    fireFormErrorAnalytics,
    renderAVS
}) {
    const { streetAddress, startTyping } = localization;
    const isUS = localeUtils.isUS();

    const isRestrictedLength = isAdsRestricted || !isAddressVerified;
    const maxLengthVal = isRestrictedLength ? FormValidator.FIELD_LENGTHS.addressAdsRestricted : FormValidator.FIELD_LENGTHS.address;

    const avsProps = hasAVS
        ? {
            'aria-autocomplete': 'list',
            'aria-controls': 'avs_listbox',
            'aria-activedescendant': highlightedAddrIndex > -1 ? `avs_result${highlightedAddrIndex}` : null,
            id: 'avs_input',
            onBlur,
            onFocus,
            onKeyDown,
            onChange
        }
        : {};

    const validateAddress = addressVal => {
        if (FormValidator.isEmpty(addressVal)) {
            if (isSubscription && fireFormErrorAnalytics) {
                fireFormErrorAnalytics(modalTitle, ErrorsUtils.getMessage(ErrorConstants.ERROR_CODES.ADDRESS1));
            }

            return ErrorConstants.ERROR_CODES.ADDRESS1;
        }

        if (!FormValidator.isValidLength(addressVal, 1, maxLengthVal)) {
            if (isSubscription && fireFormErrorAnalytics) {
                const errorCode = isRestrictedLength ? ErrorConstants.ERROR_CODES.ADDRESS1_ADS_LONG : ErrorConstants.ERROR_CODES.ADDRESS1_LONG;
                fireFormErrorAnalytics(modalTitle, ErrorsUtils.getMessage(errorCode));
            }

            return isRestrictedLength ? ErrorConstants.ERROR_CODES.ADDRESS1_ADS_LONG : ErrorConstants.ERROR_CODES.ADDRESS1_LONG;
        }

        return null;
    };

    return (
        <Box position='relative'>
            <TextInput
                {...avsProps}
                name='address1'
                onKeyUp={onKeyUp}
                label={isShortenedLoqateForm ? (address1?.length > 0 ? streetAddress : startTyping) : streetAddress}
                autoComplete={hasAVS ? (UI.isChrome() ? 'nope' : 'off') : 'address-line1'}
                autoCorrect='off'
                required={true}
                hideAsterisk={isUS || isFCReleaseThreeEnabled}
                maxLength={maxLengthVal}
                value={address1}
                ref={addressInputRef}
                validateError={validateAddress}
                data-at={Sephora.debug.dataAt('street_address_input')}
                dataAtError={Sephora.debug.dataAt('address_required_error')}
            />
            {hasAVS && renderAVS && renderAVS()}
            {isShortenedLoqateForm && !hasLoqateError && (
                <Icon
                    data-at={Sephora.debug.dataAt('search_icon')}
                    name='search'
                    size={SEARCH_ICON_SIZE}
                    color={colors.gray}
                    css={styles.searchIcon}
                    {...(!isFCReleaseThreeEnabled && {
                        style: hasAddress1Focus || (!hasAddress1Focus && !FormValidator.isEmpty(address1)) ? { display: 'none' } : null
                    })}
                />
            )}
        </Box>
    );
}

const styles = {
    searchIcon: {
        position: 'absolute',
        top: 13,
        right: space[3],
        pointerEvents: 'none'
    }
};

export default wrapFunctionalComponent(FirstAddressField, 'FirstAddressField');
